import MeetingPatterns from "components/Insights/MeetingPatterns/MeetingPatterns";
import OrganizationSummary from "components/Insights/OrganizationSummary/OrganizationSummary";
import React from "react";
import css from "../MeetingTime/AsAttendee/AsAttendee.module.scss";
import OrganizationHeader from "components/Insights/OrganizationHeader/OrganizationHeader";
import useInsights from "hooks/insights/useInsights";
import useAnalyticsStore from "stores/useAnalyticsStore";
import { filterValidMeetings, formatDuration } from "utils/insights";
import { useToggle } from "@mantine/hooks";
import { useUser } from "stores/useUser";
import { useQuery } from "react-query";
import { getMeetingsForMyOrg } from "api";
import OrganizationStrategicFocus from "components/Insights/OrganizationStrategicFocus/OrganizationStrategicFocus";
import ExternalOrgTimeCard from "components/Insights/ExternalOrgTimeCard/ExternalOrgTimeCard";
import MeetingsTableCard from "components/Insights/MeetingsTableCard/MeetingsTableCard";
import DepartmentConnectionCard from "components/DepartmentConnectionCard/DepartmentConnectionCard";
import OrgBriefRow from "components/Insights/OrgBrief/OrgBriefRow";

const MyOrg = () => {
  const [self, setSelf] = useToggle(["IncludingSelf", "ExcludingSelf"]);
  const { myOrgData, isMyOrgLoading, isMyOrgError } = useInsights();
  const { duration, setDuration } = useAnalyticsStore();
  const { user } = useUser();

  // this redis key store the complete meeting details array of org meetings
  const meetingDetailsRedisKey = `users:${
    user?.uid
  }:orgCalculations:${formatDuration(duration)}:${self}:totalMeetingsCostIds`;

  // custom feedback card data
  const {
    data: meetingDetailsData,
    isLoading: isMeetingDetailsLoading,
    isError: isMeetingDetailsError,
  } = useQuery({
    queryKey: ["meetingDetails", meetingDetailsRedisKey, user?.email],
    queryFn: () => getMeetingsForMyOrg(meetingDetailsRedisKey),
    enabled: !!user?.uid,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  if (isMyOrgError) {
    return <span>Error while loading insights</span>;
  }
  return (
    <div className={css.container} style={{ marginBottom: "1rem" }}>
      <OrganizationHeader
        snapshotDate={myOrgData?.snapshotDate}
        duration={duration}
        onDurationChange={setDuration}
        includeSelf={self === "IncludingSelf"}
        onIncludeSelfChange={setSelf}
      />
      <OrgBriefRow
        uid={user?.uid}
        snapshotDate={myOrgData?.snapshotDate}
        snapshotUpdatedAt={myOrgData?.updatedAt}
        period={formatDuration(duration)}
        perspective={self}
      />
      <OrganizationSummary
        data={myOrgData}
        period={formatDuration(duration)}
        self={self}
        uid={user?.uid}
        isLoading={isMyOrgLoading}
        isError={isMyOrgError}
      />

      <MeetingPatterns
        data={
          myOrgData?.meetingsDistribution?.[formatDuration(duration)]?.[self]
        }
        period={formatDuration(duration)}
        self={self}
        uid={user?.uid}
        isLoading={isMyOrgLoading}
        isError={isMyOrgError}
      />

      <div className={css.row}>
        <div style={{ height: "21rem" }}>
          <ExternalOrgTimeCard
            data={
              myOrgData?.["externalOrgsTime"]?.[formatDuration(duration)]?.[
                self
              ] || []
            }
            isLoading={isMyOrgLoading}
            isError={isMyOrgError}
            source="myOrg"
            duration={formatDuration(duration)}
            self={self}
            uid={user?.uid}
          />
        </div>
        <div style={{ height: "21rem" }}>
          <OrganizationStrategicFocus
            goals={
              myOrgData?.["strategicGoals"]?.[formatDuration(duration)]?.[
                self
              ] || {}
            }
            isLoading={isMyOrgLoading}
            isError={isMyOrgError}
            source="myOrg"
            duration={formatDuration(duration)}
            self={self}
            uid={user?.uid}
          />
        </div>
      </div>

      <div className={css.row} style={{ marginTop: "2rem" }}>
        <div>
          <DepartmentConnectionCard
            data={
              myOrgData?.["departmentCollaboration"]?.[
                formatDuration(duration)
              ] || []
            }
            isLoading={isMyOrgLoading}
            isError={isMyOrgError}
            source="myOrg"
            duration={formatDuration(duration)}
            uid={user?.uid}
          />
        </div>
      </div>

      <MeetingsTableCard
        data={filterValidMeetings(meetingDetailsData || [])}
        isLoading={isMeetingDetailsLoading}
        isError={isMeetingDetailsError}
      />
    </div>
  );
};

export default MyOrg;
